// components/ChatWidget.jsx
import React, { useState, useEffect } from 'react'; // ✅ Removed unused 'useRef'
import { io } from 'socket.io-client';
import {
    MainContainer,
    ChatContainer,
    MessageList,
    Message,
    MessageInput,
    Avatar,
    ConversationHeader,
    TypingIndicator
} from '@chatscope/chat-ui-kit-react';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import { Fab, Badge } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:6001';

function ChatWidget() {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isTyping, setIsTyping] = useState(false);
    const [socket, setSocket] = useState(null);
    const [customerId, setCustomerId] = useState(null);
    const [isConnected, setIsConnected] = useState(false);

    // Initialize socket connection
    useEffect(() => {
        const newSocket = io(SOCKET_URL, {
            transports: ['websocket', 'polling']
        });
        setSocket(newSocket);

        const storedId = localStorage.getItem('chat_customer_id');
        const id = storedId || `customer_${Date.now()}`;
        if (!storedId) {
            localStorage.setItem('chat_customer_id', id);
        }
        setCustomerId(id);

        newSocket.on('connect', () => {
            console.log('✅ Connected to chat server');
            setIsConnected(true);
            newSocket.emit('identify', {
                role: 'customer',
                customerId: id
            });
        });

        newSocket.on('disconnect', () => {
            console.log('❌ Disconnected from chat server');
            setIsConnected(false);
        });

        newSocket.on('receive_message', (message) => {
            console.log('📩 New message:', message);
            setMessages(prev => [...prev, {
                message: message.text,
                sentTime: message.time || 'just now',
                sender: message.sender === 'admin' ? 'Bot' : 'User',
                direction: message.sender === 'admin' ? 'incoming' : 'outgoing'
            }]);
            if (!isOpen) {
                setUnreadCount(prev => prev + 1);
            }
        });

        newSocket.on('admin_typing', (data) => {
            setIsTyping(data.isTyping);
        });

        return () => {
            newSocket.close();
        };
    }, [isOpen]); // ✅ Added 'isOpen' to dependency array

    const handleSend = (message) => {
        if (!message || !socket || !isConnected) return;

        const messageData = {
            message: message,
            customerId: customerId,
            sender: 'customer'
        };

        socket.emit('send_message', messageData);

        setMessages(prev => [...prev, {
            message: message,
            sentTime: 'just now',
            sender: 'User',
            direction: 'outgoing'
        }]);
    };

    const toggleChat = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            setUnreadCount(0);
        }
    };

    return (
        <>
            {/* Chat Button */}
            <Fab
                onClick={toggleChat}
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    bgcolor: '#25D366',
                    color: 'white',
                    '&:hover': { bgcolor: '#1DA851' },
                    zIndex: 9999,
                    boxShadow: '0 4px 20px rgba(37, 211, 102, 0.4)',
                    width: 60,
                    height: 60,
                }}
            >
                <Badge badgeContent={unreadCount} color="error" invisible={unreadCount === 0}>
                    <ChatIcon sx={{ fontSize: 28 }} />
                </Badge>
            </Fab>

            {/* Chat Window */}
            {isOpen && (
                <div style={{
                    position: 'fixed',
                    bottom: 100,
                    right: 24,
                    width: 380,
                    height: 500,
                    maxHeight: '70vh',
                    zIndex: 9998,
                    borderRadius: 16,
                    overflow: 'hidden',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                    animation: 'slideUp 0.3s ease-out',
                }}>
                    <MainContainer style={{ height: '100%' }}>
                        <ChatContainer>
                            <ConversationHeader>
                                <ConversationHeader.Back />
                                <Avatar name="Support" src="/static/images/uswellness.jpg" />
                                <ConversationHeader.Content>
                                    <span style={{ fontWeight: 'bold' }}>Live Support</span>
                                    <span style={{ fontSize: '0.8rem', color: isConnected ? '#4CAF50' : '#ff6b6b' }}>
                                        {isConnected ? '● Online' : '● Offline'}
                                    </span>
                                </ConversationHeader.Content>
                                <ConversationHeader.Actions>
                                    <span onClick={toggleChat} style={{ cursor: 'pointer' }}>✕</span>
                                </ConversationHeader.Actions>
                            </ConversationHeader>

                            <MessageList
                                typingIndicator={
                                    isTyping ? <TypingIndicator content="Admin is typing..." /> : null
                                }
                            >
                                {messages.length === 0 && (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '40px 20px',
                                        color: '#999'
                                    }}>
                                        <div style={{ fontSize: 48, marginBottom: 16 }}>👋</div>
                                        <div>Send us a message and we'll get back to you!</div>
                                    </div>
                                )}
                                {messages.map((msg, i) => (
                                    <Message
                                        key={i}
                                        model={{
                                            message: msg.message,
                                            sentTime: msg.sentTime,
                                            sender: msg.sender,
                                            direction: msg.direction,
                                            position: 'single'
                                        }}
                                    >
                                        <Avatar name={msg.sender} />
                                    </Message>
                                ))}
                            </MessageList>

                            <MessageInput
                                placeholder={isConnected ? "Type a message..." : "Connecting..."}
                                onSend={handleSend}
                                disabled={!isConnected}
                                attachButton={false}
                            />
                        </ChatContainer>
                    </MainContainer>
                </div>
            )}
        </>
    );
}

export default ChatWidget;